import { useState } from 'react'
import { IAuditState } from '../../../global/types/audit'
import { AI_DECISION } from '../../../global/constants/audit'
import styles from './styles.module.css'

interface AISummaryBannerProps {
  auditState: IAuditState | null
  onManualReviewChange: (enabled: boolean) => void
}

export default function AISummaryBanner({
  auditState,
  onManualReviewChange,
}: AISummaryBannerProps) {
  const [manualReviewEnabled, setManualReviewEnabled] = useState(false)
  const [showPassedSections, setShowPassedSections] = useState(false)

  // Calculate AI review stats
  const stats = calculateAIStats(auditState)

  // Don't render if no AI reviews exist
  if (stats.totalReviewed === 0) {
    return null
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = e.target.checked
    setManualReviewEnabled(enabled)
    onManualReviewChange(enabled)
  }

  // Format section keys for display (remove hyphens, title case)
  const formatSectionName = (key: string) => {
    return key.split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }

  return (
    <div className={styles.aiSummaryBanner}>
      <div className={styles.aiSummaryContent}>
        <div className={styles.aiSummaryText}>
          <span className={styles.aiIcon}>🤖</span>
          <strong style={{ marginLeft: '8px' }}>AI Review:</strong>
          <span style={{ marginLeft: '8px' }}>{stats.totalReviewed} sections</span>
          {stats.passed > 0 && (
            <span style={{ marginLeft: '16px', position: 'relative' }}>
              <span style={{ color: 'var(--status-completed-accent)', fontWeight: 600 }}>
                ✓ {stats.passed} passed
              </span>
              <span
                className={styles.passedSectionsInfo}
                onMouseEnter={() => setShowPassedSections(true)}
                onMouseLeave={() => setShowPassedSections(false)}
              >
                ℹ️
              </span>
              {showPassedSections && (
                <div className={styles.passedSectionsPopup}>
                  {stats.passedSections.map(formatSectionName).join(', ')}
                </div>
              )}
            </span>
          )}
          {stats.failed > 0 && (
            <span style={{ color: 'var(--status-late-accent)', marginLeft: '16px', fontWeight: 600 }}>
              ⚠ {stats.failed} flagged
            </span>
          )}
        </div>
        {stats.passed > 0 && (
          <label className={styles.aiSummaryCheckboxContainer}>
            <input
              type="checkbox"
              checked={manualReviewEnabled}
              onChange={handleCheckboxChange}
            />
            <span style={{ marginLeft: '8px' }}>Review all manually</span>
          </label>
        )}
      </div>
    </div>
  )
}

function calculateAIStats(auditState: IAuditState | null): {
  totalReviewed: number
  passed: number
  failed: number
  passedSections: string[]
} {
  if (!auditState) {
    return { totalReviewed: 0, passed: 0, failed: 0, passedSections: [] }
  }

  let passed = 0
  let failed = 0
  const passedSections: string[] = []

  Object.entries(auditState).forEach(([sectionKey, section]) => {
    if (section.aiReview) {
      if (section.aiReview.decision === AI_DECISION.pass) {
        passed++
        passedSections.push(sectionKey)
      } else if (section.aiReview.decision === AI_DECISION.fail) {
        failed++
      }
    }
  })

  return {
    totalReviewed: passed + failed,
    passed,
    failed,
    passedSections,
  }
}
